import { Box, CircularProgress, Grid } from '@mui/material'
import type { GridRowId, GridRowSelectionModel } from '@mui/x-data-grid'
import { useContext, useEffect, useState } from 'react'
import { CustomTitle } from '../components/CustomTitile'
import { Footer } from '../components/Footer'
import { ModalConfirmDialog } from '../components/ModalConfirmDialog'
import { UserAddButton } from '../components/UserAddButton'
import { UserDelButton } from '../components/UserDelButton'
import { UserList } from '../components/UserList'
import { UserPaper } from '../components/UserPaper'
import { loadStatus } from '../constants/loadStatus'
import { useAuth } from '../hooks/useAuth'
import { useC104AdministratorsGet } from '../hooks/useC104AdministratorsGet'
import { useC105AdministratorAdd } from '../hooks/useC105AdministratorAdd'
import { useC106AdministratorDelete } from '../hooks/useC106AdministratorDelete'
import type { AdministratorInfo } from '../interfaces/User'
import { LanguageContext } from '../util/LanguageContext'
import { VersionContext } from '../util/VersionContext'
import { SideMenu } from './SideMenu'

// 管理者情報画面
export const User = () => {
  // hooks
  const auth = useAuth()
  const c104 = useC104AdministratorsGet()
  const c105 = useC105AdministratorAdd()
  const c106 = useC106AdministratorDelete()
  // 文字列変換用
  const translation = useContext(LanguageContext)

  const { version } = useContext(VersionContext)

  // ローディングステータス監視
  useEffect(() => {
    if (
      c104.loading === loadStatus.LOADING ||
      c105.loading === loadStatus.LOADING ||
      c106.loading === loadStatus.LOADING
    ) {
      auth.setIsDisable(true)
    } else {
      auth.setIsDisable(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c104.loading, c105.loading, c106.loading])
  // ダイアログ
  const [openDialog, setOpenDialog] = useState(false)
  const [errorDialog, setErrorDialog] = useState('')

  // ユーザー配列
  const [users, setUsers] = useState<AdministratorInfo[]>([])
  const [selectedUsers, setSelectedUsers] = useState<AdministratorInfo[]>([])
  const [selectedLoginId, setSelectedLoginId] = useState('')
  const [isDeleteDisabled, setIsDeleteDisabled] = useState(true)
  const [gridSelection, setGridSelection] = useState<GridRowId[]>([])

  const clearSelection = () => {
    setGridSelection([])
    setSelectedUsers([])
  }
  // 起動時
  useEffect(() => {
    const fetchData = async () => {
      // 管理者情報一覧取得
      const result = await c104.execAdministratorsGet()
      if (result.success) {
        if (result.info) {
          setUsers(result.info!)
        }
      } else {
        setErrorDialog(result.message!)
        setOpenDialog(true)
      }
    }

    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 管理者追加ハンドラ
  const userAddHandler = async (
    rhq: string,
    salesCompany: string,
    loginId: string,
    password: string,
    role: number,
  ) => {
    // 管理者追加
    const result = await c105.execAdministratorAdd(rhq, salesCompany, loginId, password, role)

    if (result.success) {
      // console.log("execUserAdd.");
      // 管理者情報一覧取得
      const result = await c104.execAdministratorsGet()
      if (result.success) {
        // console.log("execAdministratorsGet.");
        if (result.info) {
          setUsers(result.info!)
        }
      } else {
        setErrorDialog(result.message!)
        setOpenDialog(true)
      }
    } else {
      setErrorDialog(result.message!)
      setOpenDialog(true)
    }
  }
  // 管理者削除ハンドラ
  const userDelHandler = async (loginId: string) => {
    let deleteCounter = 0
    await Promise.all(
      selectedUsers.map(async row => {
        // 管理者削除
        const result = await c106.execAdministratorDelete(row.login_id)
        if (result.success) {
          deleteCounter++
        } else {
          setErrorDialog(result.message!)
          setOpenDialog(true)
        }
      }),
    )

    if (deleteCounter > 0) {
      clearSelection()
      // ユーザー一覧取得
      const result = await c104.execAdministratorsGet()
      if (result.success) {
        if (result.info) {
          setUsers(result.info!)
        } else {
          setUsers([])
        }
      } else {
        setErrorDialog(result.message!)
        setOpenDialog(true)
      }
    }
  }

  const handleSelectionModelChange = (selectionModel: GridRowSelectionModel) => {
    const selectedIDs = new Set(selectionModel)
    setGridSelection(selectionModel)
    const selectedRowData = users.filter(row => selectedIDs.has(row.id))
    setSelectedUsers(selectedRowData)
  }

  // 選択ユーザーの監視
  useEffect(() => {
    setIsDeleteDisabled(
      selectedUsers.length === 1 && selectedUsers[0].login_id !== auth.loginId ? false : true,
    )
    setSelectedLoginId(selectedUsers.length === 1 ? selectedUsers[0].login_id : '')
  }, [selectedUsers, auth.loginId])

  const handleDialogClose = () => {
    setErrorDialog('')
    setOpenDialog(false)
  }

  //多言語化対応
  const userLanguage = navigator.language || navigator.languages[0]
  const baseLanguage = userLanguage.split('-')[0]
  let deleteButtonPosition: number
  let addButtonPosition: number
  switch (baseLanguage) {
    case 'de':
    case 'sv':
      deleteButtonPosition = 1340
      addButtonPosition = 1550
      break
    default:
      deleteButtonPosition = 1390
      addButtonPosition = 1600
  }

  return (
    <>
      {openDialog && (
        <ModalConfirmDialog
          open={openDialog}
          text={errorDialog}
          onClick={() => handleDialogClose()}
        />
      )}

      <Box sx={{ display: 'flex', p: 0 }}>
        <Box sx={{ p: 0, width: '338px', flexShrink: 0 }}>
          <SideMenu />
        </Box>
        <Box sx={{ flexGrow: 1, overflowX: 'auto' }} display="flex" flexDirection="column">
          <Box marginTop={13} display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 9999,
              }}
            >
              {(c104.loading === loadStatus.LOADING ||
                c105.loading === loadStatus.LOADING ||
                c106.loading === loadStatus.LOADING) && <CircularProgress />}
            </Box>
            <Box>
              <CustomTitle>{translation['label_admin_info']}</CustomTitle>
            </Box>

            {(auth.role === 2 /*2:デバイス管理者(RHQ Administrator)*/ ||
              auth.role === 4 /*4:京セラ社員*/ ||
              auth.role === 0) /*root管理者*/ && (
              <>
                <Box
                  position="fixed"
                  left={deleteButtonPosition}
                  sx={{
                    '@media (max-width: 1832px)': {
                      left: 'auto',
                      right: '230px',
                    },
                  }}
                >
                  <UserDelButton
                    title={translation['button_title_administrator']}
                    loginId={selectedLoginId}
                    isDisabled={isDeleteDisabled}
                    onDel={userDelHandler}
                  >
                    {translation['button_delete']}
                  </UserDelButton>
                </Box>
                <Box
                  position="fixed"
                  left={addButtonPosition}
                  sx={{
                    '@media (max-width: 1832px)': {
                      left: 'auto',
                      right: '20px',
                    },
                  }}
                >
                  <UserAddButton
                    title={translation['button_title_administrator']}
                    onAdd={userAddHandler}
                  >
                    {translation['button_add_new']}
                  </UserAddButton>
                </Box>
              </>
            )}
          </Box>
          <Box
            sx={{
              marginTop: '40px',
            }}
          >
            <UserPaper>
              <Grid container spacing={2}>
                <Grid item lg={6} xl={4} />

                <UserList
                  items={users}
                  handleSelectionModelChange={handleSelectionModelChange}
                  gridSelection={gridSelection}
                />
              </Grid>
            </UserPaper>
          </Box>
        </Box>
      </Box>
      <Footer />
    </>
  )
}
